import { useCallback, useMemo } from "react"
import type React from "react"
import ListItemDropDown from "components/list-item-dropdown"
import type { ListItemDropDownOption } from "components/list-item-dropdown/types"
import GraphTemplateSelector from "connected-components/graph/components/template-selector"
import PeriodDisplay from "connected-components/dashboard/components/period-display"
import { OK } from "helpers/data-statuses"
import { useCategorySync } from "connected-components/graph/hooks/use-category-sync"
import { useContentSetsMetadata } from "resources/content-sets"
import { useGraphTemplateChart } from "../../hooks/use-graph-template-chart"
import { GRAPH_CONTENT_SET_TEMPLATE_KEY, GRAPH_CATEGORY_DEFAULTS } from "../template-chart-config"
import { useActiveContentSet } from "./use-active-content-set"

interface ListItemDropDownProps {
  label: string
  value: string | null
  setValue: (id: string) => void
  options: ListItemDropDownOption[]
  alignMenu?: "left" | "right"
  disablePortal?: boolean
  listSubheader?: string
  classes?: object
  disabled?: boolean
  disableSubHeader?: boolean
  disabledTooltip?: string
  listAction?: unknown
}

const TypedListItemDropDown = ListItemDropDown as React.ComponentType<ListItemDropDownProps>

export default function ContentSetHeader() {
  const { selectedCategory, setSelectedCategory, isPreferencesLoaded } = useGraphTemplateChart(
    GRAPH_CONTENT_SET_TEMPLATE_KEY,
    GRAPH_CATEGORY_DEFAULTS,
  )
  const [, contentSetsStatus] = useContentSetsMetadata()

  const { activeContentSet } = useActiveContentSet()

  const categoryOptions = useMemo((): ListItemDropDownOption[] => {
    const basicOptions: ListItemDropDownOption[] = [{ id: "total", primaryText: "All Factors" }]
    if (!activeContentSet?.categories?.length) return basicOptions

    const modelCategories = activeContentSet.categories.map(category => ({
      id: category.id,
      primaryText: category.name,
    }))
    return [...basicOptions, ...modelCategories]
  }, [activeContentSet])

  // Gate on both: isPreferencesLoaded ensures a saved category has been written to the slice
  // before sync runs; contentSetsStatus === OK ensures the full category list is available.
  // Gating on contentSetsStatus alone risks a race where a saved category arrives before content
  // sets resolve, options are still [total], and sync resets the saved value to "total".
  const isCategoryOptionsReady = isPreferencesLoaded && contentSetsStatus === OK
  useCategorySync(categoryOptions, selectedCategory, setSelectedCategory, isCategoryOptionsReady)

  const handleCategoryChange = useCallback((category: string) => {
    setSelectedCategory(category)
  }, [setSelectedCategory])

  return (
    <>
      <GraphTemplateSelector />
      <PeriodDisplay isPerformance={false} />
      <TypedListItemDropDown
        label="Category"
        value={selectedCategory}
        setValue={handleCategoryChange}
        options={categoryOptions}
        alignMenu="left"
        disablePortal
      />
    </>
  )
}
